import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismBanner } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { t } from 'ember-intl';
import type { TablePricingManagerCustomColumn } from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager';
import type { Carate } from 'lumen/models';
import { OVERRIDE_TYPES } from 'lumen/utils/constants/pricing-assistant-constants';
import { getPushErrorTranslation } from 'lumen/utils/pricing-assistant/push-error-message-translation';

import styles from './pricing-assistant-table-tooltip.module.css';

interface HotelPricingAssistantTableTooltipsTooltipOwnRateCellSignature {
  Args: {
    carate: Carate;
    column: TablePricingManagerCustomColumn;
    isPriceLoading?: boolean;
  };
}

export default class TooltipOwnRateCell extends Component<HotelPricingAssistantTableTooltipsTooltipOwnRateCellSignature> {
  @service declare dates: Services['dates'];
  @service declare intl: Services['intl'];

  get formattedDate(): string {
    return this.args.column.date.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );
  }

  get isExactPriceOverride(): boolean {
    return (
      this.args.carate.showOverride &&
      this.args.carate.override?.type === OVERRIDE_TYPES.EXACT_PRICE
    );
  }

  get pushErrorMessage(): string {
    return getPushErrorTranslation(
      this.args.carate.carecommendation?.pushResultMessage,
      this.intl,
    );
  }

  get recommendationRowLabel(): string {
    if (this.isExactPriceOverride) {
      return this.intl.t('pricing_assistant.label.recommended_price');
    }
    return this.intl.t('pricing_manager.next_optimization_price');
  }

  get recommendationRowPrice(): string | null {
    const { carate } = this.args;

    if (!carate.carecommendation) {
      return this.intl.t('pricing_manager.current_price_is_optimal');
    }

    // no override, recommendation matches current rate → price is already optimal.
    if (
      !carate.hasOverride &&
      carate.carecommendation.priceToShow === carate.value
    ) {
      return this.intl.t('pricing_manager.current_price_is_optimal');
    }

    // exact price override → show the raw recommendation
    if (this.isExactPriceOverride) {
      if (
        carate.override!.value ===
        carate.carecommendation.actualRecommendedValue
      ) {
        return this.intl.t('pricing_manager.current_price_is_optimal');
      }
      return carate.carecommendation.formattedActualRecommendedValue;
    }

    // discount → "Automation off"
    if (
      carate.showOverride &&
      !(carate.override?.type === OVERRIDE_TYPES.PRICE_RANGE)
    ) {
      return this.intl.t('pricing_manager.automation_off');
    }

    return carate.carecommendation.formattedPriceToShow;
  }

  get showException(): boolean {
    return Boolean(this.args.carate.carecommendation?.hasException);
  }

  <template>
    <EmberTooltip
      @side="bottom"
      @tooltipClass="base-tooltip {{styles.container}}"
    >
      <div class="{{styles.header}} flex items-center justify-between p-400">
        <h4>{{this.formattedDate}}</h4>
      </div>
      {{#if this.showException}}
        <PrismBanner
          @bannerMessage={{this.pushErrorMessage}}
          @bannerStyle="critical"
          @bannerTitle={{t "pricing_assistant.exception.action_state.title"}}
          @icon="circle-minus"
          @position="top"
        />
      {{/if}}
      <table class="default-table {{styles.table}}">
        <tbody>
          {{! Row for room name }}
          <tr>
            <td class="{{styles.table-row-cell}} text-200--regular">
              <p class="text-200--regular">
                {{t "pricing_manager.room"}}
              </p>
              <p class="text-200--regular truncate">
                {{@carate.roomName}}
              </p>
            </td>
          </tr>

          {{! Row for current PMS price }}
          <tr>
            <td class="{{styles.table-row-cell}} text-200--regular">
              <p class="text-200--regular">
                {{t "pricing_manager.current_pms_price"}}
              </p>
              {{#if @isPriceLoading}}
                <div class="loading-pulse w-1/6"></div>
              {{else}}
                <div class="text-200--regular flex flex-col items-end">
                  <p class="tabular-nums">
                    {{@carate.formattedRate}}
                  </p>
                  {{#if @carate.carecommendation.formattedCurrentDiscount}}
                    <p class="text-neutral-subdued">
                      {{t
                        "pricing_manager.x_percent_discount"
                        percentage=@carate.carecommendation.formattedCurrentDiscount
                      }}
                    </p>
                  {{/if}}
                </div>
              {{/if}}
            </td>
          </tr>

          {{! Row for next optimization price / recommended price }}
          <tr>
            <td class="{{styles.table-row-cell}} text-200--regular">
              <p class="text-200--regular">
                {{#if this.showException}}
                  {{t "pricing_manager.failed_price"}}
                {{else}}
                  {{this.recommendationRowLabel}}
                {{/if}}
              </p>
              {{#if @isPriceLoading}}
                <div class="loading-pulse w-1/6"></div>
              {{else}}
                <div class="text-200--regular flex flex-col items-end">
                  <p class="tabular-nums">
                    {{this.recommendationRowPrice}}
                  </p>
                  {{#if @carate.carecommendation.formattedRecommendedDiscount}}
                    <p class="text-neutral-subdued">
                      {{t
                        "pricing_manager.x_percent_discount"
                        percentage=@carate.carecommendation.formattedRecommendedDiscount
                      }}
                    </p>
                  {{/if}}
                </div>
              {{/if}}
            </td>
          </tr>

        </tbody>
      </table>
    </EmberTooltip>
  </template>
}
